import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { MegaMenuCSSCustomProperties } from './MegaMenu.config';
import { megaMenuScrollHintSize, megaMenuScrollHintTriggerSize } from './MegaMenu.config';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { css } from '@emotion/react';
import { mobileInlinePadding, tabletInlinePadding } from './MegaMenu.styles';
var ScrollHinterCSSCustomProps;
(function (ScrollHinterCSSCustomProps) {
    ScrollHinterCSSCustomProps["topOffset"] = "--top-offset";
    ScrollHinterCSSCustomProps["bottomOffset"] = "--bottom-offset";
})(ScrollHinterCSSCustomProps || (ScrollHinterCSSCustomProps = {}));
const topThreshold = megaMenuScrollHintTriggerSize;
const bottomThreshold = topThreshold;
export const MegaMenuScrollHinter = ({ children, isSecondary, ...props }) => {
    const containerRef = useRef(null);
    const scrollWrapperRef = useRef(null);
    const [topOffset, setTopOffset] = useState(0);
    const [bottomOffset, setBottomOffset] = useState(0);
    /* todo: add logic for resize events with a debounce? */
    useEffect(() => {
        const container = containerRef.current;
        const scrollWrapper = scrollWrapperRef.current;
        const handleScroll = () => {
            if (scrollWrapper) {
                const { scrollTop, scrollHeight, clientHeight } = scrollWrapper;
                const bottomOffsetValue = scrollHeight - scrollTop - clientHeight;
                const isBottomThresholdReached = bottomOffsetValue <= bottomThreshold;
                const isTopThresholdReached = scrollTop >= topThreshold;
                setTopOffset(isTopThresholdReached ? 1 : scrollTop / topThreshold);
                setBottomOffset(isBottomThresholdReached
                    ? 1 - (bottomThreshold - bottomOffsetValue) / bottomThreshold
                    : 1);
            }
        };
        handleScroll();
        if (container && scrollWrapper) {
            scrollWrapper.addEventListener('scroll', handleScroll);
            return () => {
                scrollWrapper.removeEventListener('scroll', handleScroll);
            };
        }
    }, [children]);
    return (React.createElement(Container, { ref: containerRef, style: {
            [ScrollHinterCSSCustomProps.topOffset]: topOffset,
            [ScrollHinterCSSCustomProps.bottomOffset]: bottomOffset,
        }, ...props, isSecondary: isSecondary },
        React.createElement(ScrollWrapper, { ref: scrollWrapperRef, isSecondary: isSecondary }, children)));
};
const Container = styled.div `
  ${MediaQueries.untilDesktop} {
    block-size: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin-inline: -${mobileInlinePadding};
    padding-inline: ${mobileInlinePadding} 0;

    &::after {
      content: '';
      position: absolute;
      inset: 0 ${Spacings.large} 0 0;
      background: linear-gradient(
        to bottom,
        var(${MegaMenuCSSCustomProperties.background}),
        transparent calc(${megaMenuScrollHintSize}px * var(${ScrollHinterCSSCustomProps.topOffset})),
        transparent
          calc(
            100% - (${megaMenuScrollHintSize}px * var(${ScrollHinterCSSCustomProps.bottomOffset}))
          ),
        var(${MegaMenuCSSCustomProperties.background})
      );
      pointer-events: none;
    }

    ${MediaQueries.tablet} {
      margin-inline: -${tabletInlinePadding};
      padding-inline: ${tabletInlinePadding} 0;
    }
  }

  ${({ isSecondary }) => isSecondary === false &&
    css `
      ${MediaQueries.desktop} {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    `}

  ${({ isSecondary }) => isSecondary &&
    css `
      block-size: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      ${MediaQueries.desktop} {
        inline-size: 100%;
      }
    `}
`;
const ScrollWrapper = styled.div `
  ${MediaQueries.untilDesktop} {
    overflow: auto;
    height: auto;
    margin-block-start: auto;
    max-block-size: 100%;
  }

  ${MediaQueries.mobile} {
    padding-inline-end: ${mobileInlinePadding};
  }

  ${MediaQueries.tablet} {
    padding-inline-end: ${tabletInlinePadding};
  }

  ${MediaQueries.desktop} {
    padding-inline-end: 0;
  }

  ${({ isSecondary }) => isSecondary &&
    css `
      ${MediaQueries.untilDesktop} {
        display: flex;
        flex-direction: column;
        gap: ${Spacings.large};
      }

      ${MediaQueries.desktop} {
        display: grid !important;
        gap: 0 ${Spacings.xLarge};
        grid-template-columns: repeat(13, 1fr);
        inline-size: 100%;

        > * {
          grid-column: span 4;
          min-width: 0;
        }
      }

      ${MediaQueries.desktopWide} {
        grid-template-columns: repeat(14, 1fr);
      }
    `}
`;
